/* "אפשר לקנות את זה?": סכום, מתי ואיך משלמים, ותשובה מיד. לא נשמר כלום */
import { useMemo, useState } from 'react';
import { fMoney, wD, wDYY, todayISO, DAYS, MONTHS } from '../../lib/format.js';
import { periodEnd } from '../../domain/periods.js';
import { affordCheck, firstHit, PAY_OPTIONS } from '../../domain/afford.js';
import { Sheet, Seg } from '../../components/ui.jsx';
import { openModal } from '../../app/nav.js';
import { useApp } from '../../store/ui.js';

/** init: ערכים התחלתיים (סכום, תאריך, תשלומים), למשל מבדיקת העשן */
export function openAfford(init) {
  openModal(() => <AffordSheet init={init} />);
}

/** "יום ראשון, 1 בנובמבר": שדה התאריך מוצג בפורמט של הטלפון, לפעמים אמריקאי */
function dayName(iso) {
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d) ? '' : 'יום ' + DAYS[d.getDay()] + ', ' + d.getDate() + ' ב' + MONTHS[d.getMonth()];
}

/** "בסוף 10.11: X במקום Y": ההבדל ביתרה בסוף התקופה שבה הקנייה מתחילה לרדת */
function endLine(h) {
  return 'בסוף התקופה (' + wD(periodEnd(h.k)) + '): ' + fMoney(h.endAfter.balance) + ' במקום ' + fMoney(h.endBefore.balance) + '.';
}

/** התשובה במילים: כן, אפשר אבל, או לא. התשובה עצמה לפי הנקודה הנמוכה (מסגרת, מינוס), התיאור לפי סוף התקופה */
function verdict(r) {
  const d = endLine(firstHit(r));
  if (r.level === 'ok') return { t: '✓ כן, אפשר', d };
  if (r.level === 'no') return { t: '✗ לא כדאי', d };
  if (r.intoMinus) return { t: 'אפשר, אבל תיכנסו למינוס', d };
  return { t: 'אפשר, אבל המינוס יעמיק', d };
}

/** אזהרה על אמצע החודש, רק כשהיא חשובה: הקנייה מכניסה למינוס, מקרבת למסגרת או חוצה אותה */
function lowWarning(r) {
  const lw = r.lw, when = wD(lw.date), lim = r.lim;
  if (r.level === 'ok' && lw.balance >= 0) return null;
  const pct = lim && lw.balance < 0 ? Math.round((-lw.balance / lim) * 100) : 0;
  if (lim && lw.balance < -lim) return '⚠️ באמצע הדרך, ב-' + when + ', החשבון ירד ל-' + fMoney(lw.balance) + ': ' + fMoney(-lim - lw.balance) + ' מעבר למסגרת.';
  if (r.intoMinus) return '⚠️ באמצע הדרך, ב-' + when + ', החשבון ירד ל-' + fMoney(lw.balance) + '. בלי הקנייה הוא לא היה יורד מתחת ל-0.';
  if (pct >= 80) return '⚠️ באמצע הדרך, ב-' + when + ', החשבון ירד ל-' + fMoney(lw.balance) + ': ' + pct + '% מהמסגרת.';
  return null;
}

function AffordSheet({ init }) {
  useApp();
  const [f, setF] = useState({ amount: '', date: todayISO(), src: 'credit', payments: 1, ...init });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const amount = parseFloat(f.amount) || 0;
  const r = useMemo(() => affordCheck({ amount, date: f.date, src: f.src, payments: f.src === 'credit' ? Number(f.payments) : 1 }), [amount, f.date, f.src, f.payments]);
  const v = r && verdict(r), warn = r && lowWarning(r);
  const cls = r ? { ok: 'ok', warn: 'warn', no: 'bad' }[r.level] : '';
  const first = r && r.buy[0], last = r && r.buy[r.buy.length - 1];
  return (
    <Sheet title="אפשר לקנות את זה?">
      <div className="form-row">
        <div><label className="lbl">כמה (₪)</label>
          <input type="number" inputMode="decimal" min="0" value={f.amount} onChange={(e) => set('amount')(e.target.value)} placeholder="4,000" autoFocus style={{ fontSize: '1.2rem', fontWeight: 800 }} /></div>
        <div><label className="lbl">מתי</label><input type="date" value={f.date} min={todayISO()} onChange={(e) => e.target.value && set('date')(e.target.value)} />
          <div className="note mt6">{dayName(f.date)}</div></div>
      </div>
      <label className="lbl">איך משלמים</label>
      <Seg value={f.src} onChange={set('src')} options={[['credit', '💳 כרטיס אשראי'], ['cash', '🏦 מהעו"ש']]} />
      {f.src === 'credit' && (
        <div className="mt10">
          <label className="lbl">תשלומים</label>
          <select value={f.payments} onChange={(e) => set('payments')(Number(e.target.value))} style={{ width: '100%' }}>
            {PAY_OPTIONS.map((n) => <option key={n} value={n}>{n === 1 ? 'תשלום אחד' : n + ' תשלומים' + (amount ? ' (' + fMoney(amount / n) + ' בחודש)' : '')}</option>)}
          </select>
        </div>
      )}
      {!amount && <div className="note mt12">כתבו סכום, והתשובה תופיע כאן. לא נשמר כלום: זו רק בדיקה.</div>}
      {r && (
        <div className={'callout ' + cls} style={{ marginTop: 16 }}>
          <b style={{ fontSize: '1.1rem' }}>{v.t}</b>
          <div>{v.d}</div>
          {warn && <div className="mt6" style={{ fontWeight: 700 }}>{warn}</div>}
          <div className="aff-tbl">
            <span className="th">יתרה בסוף התקופה</span><span className="th num">בלי הקנייה</span><span className="th num">עם הקנייה</span>
            {r.periods.map((x, i) => [
              <span key={x.k + 'a'}>{wD(periodEnd(x.k))}{i === 0 ? ' (התקופה הזו)' : ''}</span>,
              <span key={x.k + 'b'} className="num">{fMoney(x.endBefore.balance)}</span>,
              <span key={x.k + 'c'} className="num" style={{ color: x.endAfter.balance < x.endBefore.balance - 0.5 ? (x.endAfter.balance < 0 ? 'var(--red)' : 'var(--ink)') : undefined }}>
                {fMoney(x.endAfter.balance)}</span>,
            ])}
          </div>
          {r.perDay && r.inCur > 0 && <div className="mt6">מותר להוציא היום ירד מ-<strong>{fMoney(Math.max(0, Math.floor(r.perDay.before / 10) * 10))}</strong> ל-<strong>{fMoney(Math.max(0, Math.floor(r.perDay.after / 10) * 10))}</strong> ביום.</div>}
          <div className="note mt6">
            {r.buy.length > 1 ? r.buy.length + ' תשלומים של ' + fMoney(first.amount) + ', מ-' + wD(first.iso) + ' עד ' + wDYY(last.iso) : 'יורד ' + (f.src === 'credit' ? 'בחיוב הכרטיס של ' : 'ב-') + wD(first.iso)}.
          </div>
        </div>
      )}
      {r && (r.better || r.later) && (
        <div className="mt12">
          <div className="sec" style={{ fontSize: '.95rem', marginBottom: 6 }}>מה יעזור</div>
          {r.better && (
            <button className="task w100" onClick={() => set('payments')(r.better.payments)}>
              <span className="task-ico">💳</span>
              <div className="grow" style={{ textAlign: 'start' }}><b>{r.better.payments} תשלומים</b><div className="note">{endLine(firstHit(r.better))}</div></div>
            </button>
          )}
          {r.later && (
            <button className="task w100 mt6" onClick={() => set('date')(r.later.date)}>
              <span className="task-ico">📅</span>
              <div className="grow" style={{ textAlign: 'start' }}><b>לחכות עד {wD(r.later.date)}</b><div className="note">{endLine(firstHit(r.later))}</div></div>
            </button>
          )}
        </div>
      )}
      {r && r.real && (
        <div className={'callout ' + (r.real.level === 'no' && r.level !== 'no' ? 'bad' : '')} style={{ marginTop: 12, background: r.real.level === 'no' && r.level !== 'no' ? undefined : 'var(--surface2)' }}>
          <b>ואם תוציאו כרגיל עד אז</b>
          עם ההוצאות הרגילות שעוד יגיעו (אוכל, דלק ושאר הקניות בכרטיס), בסוף התקופה ({wD(periodEnd(firstHit(r.real).k))}) היתרה תהיה בערך <b>{fMoney(Math.round(firstHit(r.real).endAfter.balance / 100) * 100)}</b>.
          {r.real.level === 'no' && r.level !== 'no' && <> ובאמצע הדרך החשבון יחרוג מהמסגרת.</>}
        </div>
      )}
      {r && <div className="note mt12">"בלי הקנייה" בתקופה הזו הוא אותו מספר כמו "בסוף התקופה" בתוכנית במסך הבית: רק מה שכבר ידוע, נכון לרגע זה. הבדיקה עד {wDYY(r.end)}, תקופה שלמה אחרי התשלום האחרון.</div>}
    </Sheet>
  );
}
